import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {DateTime} from 'luxon';
import {useSelector} from 'react-redux';
import Skeleton from 'react-loading-skeleton';
import {renderToString} from 'react-dom/server';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import PhotoSwipe from 'photoswipe';
import {useIsMediumAndUp} from '~library/hooks/useMatchQuery';
import {isHost} from '~/ViewInvitation/utils';
import {useDeletePhoto} from '~/ViewInvitation/hooks/useDeletePhoto';
import {usePhotos} from '~/ViewInvitation/hooks/usePhotos';
import {CloseLineIcon} from '~library/svg/CloseLineIcon';
import {TrashIcon} from '~library/svg/TrashIcon';
import {DownloadIcon} from '~library/svg/DownloadIcon';
import {FlagIcon} from '~library/svg/FlagIcon';
import {ZoomInIcon} from '~library/svg/ZoomInIcon';
import {ZoomOutIcon} from '~library/svg/ZoomOutIcon';
import {Typography} from '~library/atoms/Typography';
import {getTimeSince} from '~/ViewInvitation/common/TimeSince';
import {useImageProxySizer} from '~/ViewInvitation/hooks/useImageProxySizer';
import {
  PhotoDeleteConfirmationModal,
  PhotoDeleteFlagModal,
} from '~/ViewInvitation/routes/Photos/modals';

import 'photoswipe/style.css';
import 'photoswipe/dist/photoswipe.css';
import {ghost} from '~libSass/base/_exports.sass';
import 'react-loading-skeleton/dist/skeleton.css';
import './PhotoViewer.sass';

const createdDateTime = (currentImage) => {
  if (currentImage.created) {
    const createdDateTimeParsed = DateTime.fromFormat(currentImage.created, `yyyyMMdd'T'HHmmss`);
    return createdDateTimeParsed.toLocaleString(DateTime.DATE_FULL);
  }
  return undefined;
};

const trashIcon = <TrashIcon className="delete-icon" ratio={0.65} color={ghost} />;
const flagIcon = <FlagIcon className="flag-icon" ratio={0.7} color={ghost} />;
const downloadIcon = renderToString(<DownloadIcon ratio={0.75} color={ghost} />);
const zoomInIcon = <ZoomInIcon ratio={0.65} color={ghost} />;
const zoomOutIcon = renderToString(<ZoomOutIcon ratio={0.65} color={ghost} />);
const closeButton = renderToString(<CloseLineIcon ratio={1.1} color={ghost} />);

export const PhotoViewer = ({initialPhotoId, onClose, isHostModule = false}) => {
  const isMediumAndUp = useIsMediumAndUp();
  const {getDisplayUrl} = useImageProxySizer();
  const lightboxRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0); // TODO: consider modifying this state to hold photo.id as well
  const [showingDeleteMessage, setShowingDeleteMessage] = useState(false);
  const eventId = useSelector((state) => state.eventDetails?.eventId);
  const isWebview = window.is_webview;

  const getPhotosQueryArgs = useMemo(
    () => ({
      eventId,
      isHostModule,
      perPage: -1,
      includeCount: true,
    }),
    [eventId, isHostModule]
  );

  const {photos, photoCount, isLoading} = usePhotos(getPhotosQueryArgs);

  const photosListRef = useRef(photos);
  photosListRef.current = photos;
  const photoCountRef = useRef(photoCount);
  photoCountRef.current = photoCount;

  const onDelete = useCallback(() => {
    const updatedPhotoCount = photoCountRef.current;
    const newIndex =
      currentIndex >= updatedPhotoCount ? Math.max(updatedPhotoCount - 1, 0) : currentIndex;
    setCurrentIndex(newIndex);

    // Update PhotoSwipe internal index
    if (lightboxRef.current && lightboxRef.current.pswp) {
      lightboxRef.current.pswp.goTo(newIndex);
      lightboxRef.current.pswp.refreshSlideContent(newIndex);
    }
  }, [currentIndex]);

  const {canDeletePhoto, deleteCurrentPhoto, showingFlagMessage, setShowingFlagMessage} =
    useDeletePhoto({
      photos,
      photoCount,
      onClose,
      onDelete,
      setShowingDeleteMessage,
      getPhotosQueryArgs,
      currentIndex,
      isHostModule,
      isPhotoViewer: true,
    });

  const downloadPhoto = (photo) => {
    const id = isHostModule ? photo.photo_id : photo.content.photo_id;
    const url = `/ajax/event/${eventId}/posts/photos/${id}/download/`;
    const link = document.createElement('a');
    link.href = url;
    link.download = `photo_${id}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const createPhotoDetails = (currentImage) => (
    <div className="photo-viewer__photo-details">
      {currentImage.creator ? (
        <Typography
          variant="paragraph1"
          color="ghost"
          aria-label={`Photo taken by ${currentImage.creator.first_name} ${currentImage.creator.last_name}`}
          title={`Photo taken by ${currentImage.creator.first_name} ${currentImage.creator.last_name}`}
        >
          {currentImage.creator.first_name} {currentImage.creator.last_name}
        </Typography>
      ) : (
        <Skeleton width={200} />
      )}
      {currentImage.created ? (
        <Typography
          variant="paragraph4"
          color="ghost"
          arial-label={createdDateTime(currentImage)}
          title={createdDateTime(currentImage)}
        >
          {getTimeSince(currentImage.created)}
        </Typography>
      ) : (
        <Skeleton width={110} />
      )}
    </div>
  );

  const showOptions = !isHostModule || isHost();

  useEffect(() => {
    if (!isLoading && photosListRef.current.length > 0) {
      const idToUse = isHostModule ? 'photo_id' : 'id';
      const foundIndex = photos.findIndex((photo) => photo[idToUse] === initialPhotoId);
      const startIndex = foundIndex !== -1 ? foundIndex : 0;
      setCurrentIndex(startIndex);
      const config = {
        bgOpacity: 0.9,
        pswpModule: PhotoSwipe,
        showHideAnimationType: 'fade',
        preload: [1, 2, 3, 4, 5],
        clickToCloseNonZoomable: false,
        preloaderDelay: 0,
      };
      const lightbox = new PhotoSwipeLightbox(config);
      window.lightbox = lightbox;
      lightbox.on('close', () => {
        onClose();
      });
      lightbox.addFilter('numItems', () => photoCountRef.current);
      lightbox.addFilter('itemData', (itemData, index) => {
        const photo = photosListRef.current[index];
        const photoUrl = isHostModule ? photo.url : photo.content?.url;
        const width = isHostModule ? photo.width : photo.content?.width;
        const height = isHostModule ? photo.height : photo.content?.height;
        return {
          src: getDisplayUrl(photoUrl),
          alt: photo.comment || photo.post_type,
          width,
          height,
          ...photo,
        };
      });
      lightbox.on('change', () => {
        setCurrentIndex(lightbox.pswp.currIndex);
        if (showOptions) {
          const canDelete = isHostModule || canDeletePhoto(lightbox.pswp.currSlide.data);
          document.querySelector('.delete-icon').style.display = canDelete ? 'inline' : 'none';
          document.querySelector('.flag-icon').style.display = canDelete ? 'none' : 'inline';
        }
      });
      lightbox.addFilter('uiElement', (element, data) => {
        if ((isWebview || !isMediumAndUp) && data.name === 'zoom') {
          // eslint-disable-next-line no-param-reassign
          element.style.display = 'none';
        }
        return element;
      });
      lightbox.on('uiRegister', () => {
        lightbox.pswp.ui.registerElement({
          name: 'caption',
          order: 9,
          isButton: false,
          appendTo: 'root',
          html: '',
          onInit: (el) => {
            lightbox.pswp.on('change', () => {
              const currSlideElement = lightbox.pswp.currSlide.data;
              if (currSlideElement && !isHostModule) {
                // eslint-disable-next-line no-param-reassign
                el.innerHTML = renderToString(createPhotoDetails(currSlideElement));
              }
            });
          },
        });
        lightbox.pswp.ui.registerElement({
          name: 'delete-button',
          ariaLabel: 'Delete',
          order: 8,
          isButton: true,
          html: showOptions
            ? renderToString(
                <span>
                  {trashIcon}
                  {flagIcon}
                </span>
              )
            : null,
          onClick: () => {
            const canDelete = canDeletePhoto(lightbox.pswp.currSlide.data);
            if (canDelete) {
              setShowingDeleteMessage(true);
            } else {
              setShowingFlagMessage(true);
            }
          },
        });
        if (!isWebview) {
          lightbox.pswp.ui.registerElement({
            name: 'download-button',
            ariaLabel: 'Download',
            order: 9,
            isButton: true,
            html: downloadIcon,
            onClick: () => {
              downloadPhoto(lightbox.pswp.currSlide.data);
            },
          });
        }
      });
      lightbox.on('zoomPanUpdate', (data) => {
        const canZoomIn = data.slide.currZoomLevel < 1;
        document.getElementById('zoom-icon').innerHTML = canZoomIn
          ? renderToString(zoomInIcon)
          : zoomOutIcon;
      });
      lightbox.addFilter('uiElement', (element, data) => {
        if (data.name === 'close') {
          // eslint-disable-next-line no-param-reassign
          element.innerHTML = closeButton;
        }
        if (data.name === 'zoom') {
          // eslint-disable-next-line no-param-reassign
          element.innerHTML = renderToString(<span id="zoom-icon">{zoomInIcon}</span>);
        }
        return element;
      });
      lightbox.init();
      lightbox.loadAndOpen(startIndex);
      lightboxRef.current = lightbox;
      return () => {
        lightbox.destroy();
        lightboxRef.current = null;
      };
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, initialPhotoId]);

  return createPortal(
    <div className="photo-viewer">
      {showingDeleteMessage && (
        <PhotoDeleteConfirmationModal
          onApprove={deleteCurrentPhoto}
          onClose={() => setShowingDeleteMessage(false)}
          isHostModule={isHostModule}
        />
      )}
      {showingFlagMessage && <PhotoDeleteFlagModal onClose={() => setShowingFlagMessage(false)} />}
    </div>,
    document.body
  );
};
